import React from 'react';
import { Paperclip, X, Image as ImageIcon, AlertCircle, Loader2, FolderArchive } from 'lucide-react';
import { useLanguage } from '../i18n';

/**
 * Pill shown above the chat input for each attached file.
 * Supports three shapes:
 *   - Uploaded descriptor: {id, filename, mime_type, file_size, is_image}
 *   - Pending descriptor:  {...descriptor, uploading: true}
 *   - Failed descriptor:   {...descriptor, upload_error: "..."}
 *   - Legacy File object:  {name} (falls through via the ?? guards)
 *   - Unpacked ZIP (#513): {filename, archive_import: {saved_count, …}, summary}
 *     — no `id`; the server already filed every member in the File Manager.
 */
const AttachmentPill = ({ file, onRemove }) => {
    const { t } = useLanguage();
    const label = file?.filename || file?.name || 'file';
    const uploading = !!file?.uploading;
    const error = file?.upload_error;
    const isImage = !!file?.is_image || (typeof file?.mime_type === 'string' && file.mime_type.startsWith('image/'));
    const archive = file?.archive_import || null;
    const isZip = !!archive || /\.(zip|7z)$/i.test(label);

    const palette = error
        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
        : uploading
        ? 'bg-white/[0.06] text-pai-secondary'
        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';

    const Icon = error ? AlertCircle : uploading ? Loader2 : (archive ? FolderArchive : isImage ? ImageIcon : Paperclip);

    return (
        <div
            className={`flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full ${palette}`}
            title={error ? `Upload failed: ${error}` : (file?.summary || label)}
            data-testid={archive ? 'attachment-archive-pill' : undefined}
        >
            <Icon size={14} className={uploading ? 'animate-spin' : ''} />
            <span className="truncate max-w-[180px]">{label}</span>
            {uploading && (
                <span className="text-[10px] opacity-70">
                    {isZip ? t('attachArchiveUnpacking', 'unpacking…') : 'uploading…'}
                </span>
            )}
            {archive && !uploading && !error && (
                <span className="text-[10px] opacity-70" data-testid="attachment-archive-count">
                    {t('attachArchiveFiles', '{n} files saved', { n: archive.saved_count ?? 0 })}
                </span>
            )}
            {error && <span className="text-[10px] opacity-70">failed</span>}
            {/* The X is ALWAYS available — including mid-upload/processing — so a
                hung or accidental upload can be cancelled and never bricks the
                input (Cancel Upload ticket). For an in-flight upload it aborts the
                request; otherwise it just detaches the file. */}
            {onRemove && (
                <button
                    onClick={() => onRemove(file)}
                    className="opacity-70 hover:opacity-100"
                    title={uploading ? 'Cancel upload' : 'Remove'}
                    aria-label={uploading ? 'Cancel upload' : 'Remove attachment'}
                >
                    <X size={14} />
                </button>
            )}
        </div>
    );
};

export default AttachmentPill;
